import React, { useState, useEffect } from 'react';
import { Search, Filter, X, Save, Star, ChevronDown, Flame } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { supabase } from '../lib/supabase';

export interface FilterConfig {
  search: string;
  valueMin: number | null;
  valueMax: number | null;
  closeDate: 'all' | 'this_week' | 'this_month' | 'this_quarter' | 'overdue';
  probability: 'all' | 'high' | 'medium' | 'low';
  priority?: 'all' | 'hot' | 'warm' | 'cold' | 'inactive';
  source: string[];
  tags: string[];
}

interface SavedFilter {
  id: string;
  name: string;
  filters: FilterConfig;
  is_shared: boolean;
}

interface PipelineFiltersProps {
  filters: FilterConfig;
  onFiltersChange: (filters: FilterConfig) => void;
}

const PRIORITY_LEVELS = [
  { value: 'all', label: 'All Priorities', icon: '🎯' },
  { value: 'hot', label: 'Hot (75-100)', icon: '🔥' },
  { value: 'warm', label: 'Warm (50-74)', icon: '🌤️' },
  { value: 'cold', label: 'Cold (25-49)', icon: '❄️' },
  { value: 'inactive', label: 'Inactive (0-24)', icon: '💤' },
];

const VALUE_RANGES = [
  { label: 'Any Amount', min: null, max: null },
  { label: '$0 - $5,000', min: 0, max: 5000 },
  { label: '$5,000 - $25,000', min: 5000, max: 25000 },
  { label: '$25,000+', min: 25000, max: null },
];

const SOURCES = [
  'manual',
  'referral',
  'website',
  'cold_call',
  'social_media',
  'advertisement',
  'partner',
  'other',
];

export default function PipelineFilters({ filters, onFiltersChange }: PipelineFiltersProps) {
  const { selectedBrand } = useBrand();
  const [showFilters, setShowFilters] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>([]);

  useEffect(() => {
    if (selectedBrand) {
      loadSavedFilters();
      loadTags();
    }
  }, [selectedBrand]);

  const loadSavedFilters = async () => {
    if (!selectedBrand) return;

    const { data, error } = await supabase
      .from('saved_filters')
      .select('*')
      .eq('brand_id', selectedBrand.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setSavedFilters(data);
    }
  };

  const loadTags = async () => {
    if (!selectedBrand) return;

    const { data, error } = await supabase
      .from('opportunity_tags')
      .select('*')
      .eq('brand_id', selectedBrand.id)
      .order('name');

    if (!error && data) {
      setTags(data);
    }
  };

  const handleSaveFilter = async () => {
    if (!selectedBrand || !filterName.trim()) return;

    const { error } = await supabase.from('saved_filters').insert({
      brand_id: selectedBrand.id,
      name: filterName,
      filters: filters,
      is_shared: isShared,
    });

    if (!error) {
      setFilterName('');
      setIsShared(false);
      setShowSaveModal(false);
      loadSavedFilters();
    }
  };

  const handleLoadFilter = (savedFilter: SavedFilter) => {
    onFiltersChange(savedFilter.filters);
    setShowFilters(false);
  };

  const handleDeleteFilter = async (filterId: string) => {
    await supabase.from('saved_filters').delete().eq('id', filterId);
    loadSavedFilters();
  };

  const handleClearFilters = () => {
    onFiltersChange({
      search: '',
      valueMin: null,
      valueMax: null,
      closeDate: 'all',
      probability: 'all',
      source: [],
      tags: [],
    });
  };

  const isFiltered = filters.search || filters.valueMin !== null || filters.valueMax !== null ||
    filters.closeDate !== 'all' || filters.probability !== 'all' ||
    filters.source.length > 0 || filters.tags.length > 0;

  const activeFilterCount = [
    filters.search,
    filters.valueMin !== null || filters.valueMax !== null,
    filters.closeDate !== 'all',
    filters.probability !== 'all',
    filters.source.length > 0,
    filters.tags.length > 0,
  ].filter(Boolean).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            placeholder="Search by name, company, or email..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
            showFilters || isFiltered
              ? 'bg-blue-600 text-white'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-white text-blue-600 px-1.5 py-0.5 rounded-full text-xs font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>

        {isFiltered && (
          <button
            onClick={handleClearFilters}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        )}

        {savedFilters.length > 0 && (
          <div className="relative group">
            <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-50">
              <Star className="w-4 h-4" />
              Saved
              <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <div className="p-2 space-y-1">
                {savedFilters.map((sf) => (
                  <div key={sf.id} className="flex items-center justify-between group/item hover:bg-gray-50 rounded px-2 py-1.5">
                    <button
                      onClick={() => handleLoadFilter(sf)}
                      className="flex-1 text-left text-sm text-gray-700"
                    >
                      {sf.name}
                      {sf.is_shared && (
                        <span className="ml-1 text-xs text-blue-600">(Shared)</span>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteFilter(sf.id)}
                      className="opacity-0 group-hover/item:opacity-100 p-1 text-gray-400 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Value Range
              </label>
              <select
                value={`${filters.valueMin ?? ''}-${filters.valueMax ?? ''}`}
                onChange={(e) => {
                  const [min, max] = e.target.value.split('-');
                  onFiltersChange({
                    ...filters,
                    valueMin: min ? Number(min) : null,
                    valueMax: max ? Number(max) : null,
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {VALUE_RANGES.map((range) => (
                  <option key={range.label} value={`${range.min ?? ''}-${range.max ?? ''}`}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Close
              </label>
              <select
                value={filters.closeDate}
                onChange={(e) =>
                  onFiltersChange({ ...filters, closeDate: e.target.value as FilterConfig['closeDate'] })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Dates</option>
                <option value="overdue">Overdue</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="this_quarter">This Quarter</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Probability
              </label>
              <select
                value={filters.probability}
                onChange={(e) =>
                  onFiltersChange({ ...filters, probability: e.target.value as FilterConfig['probability'] })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Confidence</option>
                <option value="high">High (&gt;70%)</option>
                <option value="medium">Medium (40-70%)</option>
                <option value="low">Low (&lt;40%)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                Priority Score
              </label>
              <select
                value={filters.priority || 'all'}
                onChange={(e) =>
                  onFiltersChange({ ...filters, priority: e.target.value as FilterConfig['priority'] })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {PRIORITY_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.icon} {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source
              </label>
              <div className="relative">
                <select
                  multiple
                  value={filters.source}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                    onFiltersChange({ ...filters, source: selected });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 max-h-32"
                >
                  {SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {source.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => {
                      const newTags = filters.tags.includes(tag.id)
                        ? filters.tags.filter((t) => t !== tag.id)
                        : [...filters.tags, tag.id];
                      onFiltersChange({ ...filters, tags: newTags });
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                      filters.tags.includes(tag.id)
                        ? 'text-white'
                        : 'bg-white border-2 text-gray-700'
                    }`}
                    style={{
                      backgroundColor: filters.tags.includes(tag.id) ? tag.color : 'white',
                      borderColor: tag.color,
                    }}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2 border-t">
            <button
              onClick={() => setShowSaveModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Filter Preset
            </button>
          </div>
        </div>
      )}

      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Save Filter Preset</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter Name
                </label>
                <input
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="e.g., High Value Q4 Deals"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="share-filter"
                  checked={isShared}
                  onChange={(e) => setIsShared(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="share-filter" className="ml-2 text-sm text-gray-700">
                  Share with team
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFilter}
                disabled={!filterName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
